import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, ImageIcon, Video, FileText, DollarSign, X, Wand2, Loader2, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const NICHES = ["affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];
const TIMEFRAMES = ["today", "last 7 days", "this month", "last month"];

export default function Composer({ onPosted }) {
  const { user } = useAuth();
  const [mode, setMode] = useState("regular"); // regular | income_proof
  const [content, setContent] = useState("");
  const [niche, setNiche] = useState("affiliate");
  const [amount, setAmount] = useState("");
  const [timeframe, setTimeframe] = useState("this month");
  const [imageUrls, setImageUrls] = useState([]); // multi-image gallery (regular posts)
  const [imageCaptions, setImageCaptions] = useState([]); // parallel captions
  const [dragIdx, setDragIdx] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");   // single video or document
  const [mediaType, setMediaType] = useState(null); // 'video' | 'document' (images use imageUrls)
  const [mediaName, setMediaName] = useState("");
  const [mediaSize, setMediaSize] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("Uploading…");
  const [posting, setPosting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const imageInputRef = useRef();
  const videoInputRef = useRef();
  const docInputRef = useRef();

  const MAX_IMAGES = 10;

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (e) => {
        if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
      },
    });
    return data;
  };

  const uploadImages = async (files) => {
    if (!files?.length) return;
    // Only image files, capped at MAX_IMAGES total
    const remaining = MAX_IMAGES - imageUrls.length;
    if (remaining <= 0) return alert(`You can attach up to ${MAX_IMAGES} images.`);
    const picks = Array.from(files).slice(0, remaining);
    // Selecting images clears any existing video/doc.
    setMediaUrl(""); setMediaType(null); setMediaName(""); setMediaSize(0);
    setUploading(true);
    try {
      for (let i = 0; i < picks.length; i++) {
        setUploadLabel(`Uploading ${i + 1}/${picks.length}…`);
        setUploadPct(0);
        const data = await uploadOne(picks[i]);
        if (data?.media_type === "image" && data?.url) {
          setImageUrls((arr) => [...arr, data.url]);
          setImageCaptions((arr) => [...arr, ""]);
        }
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadLabel("Uploading…");
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    setUploadLabel("Uploading…");
    try {
      const data = await uploadOne(file);
      if (data.media_type === "image") {
        // Legacy single-image path still works — append to gallery.
        setImageUrls((arr) => (arr.length >= MAX_IMAGES ? arr : [...arr, data.url]));
        setImageCaptions((arr) => (arr.length >= MAX_IMAGES ? arr : [...arr, ""]));
        setMediaUrl(""); setMediaType(null); setMediaName(""); setMediaSize(0);
      } else {
        // video / document — clear the image gallery so only one media type per post.
        setImageUrls([]);
        setImageCaptions([]);
        setMediaUrl(data.url);
        setMediaType(data.media_type);
        setMediaName(data.filename || file.name);
        setMediaSize(data.size || file.size);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const removeImageAt = (idx) => {
    setImageUrls((arr) => arr.filter((_, i) => i !== idx));
    setImageCaptions((arr) => arr.filter((_, i) => i !== idx));
  };

  const setCaptionAt = (idx, value) => {
    setImageCaptions((arr) => arr.map((c, i) => (i === idx ? value : c)));
  };

  const moveImage = (from, to) => {
    if (from === to || from == null || to == null) return;
    setImageUrls((arr) => {
      const next = arr.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setImageCaptions((arr) => {
      const next = arr.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const removeMedia = () => {
    setMediaUrl("");
    setMediaType(null);
    setMediaName("");
    setMediaSize(0);
    setImageUrls([]);
    setImageCaptions([]);
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (docInputRef.current) docInputRef.current.value = "";
  };

  const submit = async () => {
    if (mode === "income_proof") {
      if (!amount || Number(amount) <= 0) return alert("Enter your earnings amount.");
    } else if (!content.trim() && !mediaUrl && imageUrls.length === 0) {
      return;
    }
    setPosting(true);
    try {
      const hashtags = [...content.matchAll(/#(\w+)/g)].map((m) => m[1]);
      const regularImages = mode === "regular" ? imageUrls : [];
      const regularCaptions = mode === "regular" ? imageCaptions.slice(0, regularImages.length) : [];
      const primaryImage = regularImages[0] || null;
      const payload = {
        post_type: mode,
        content,
        niche,
        hashtags,
        image_url: primaryImage,
        image_urls: regularImages,
        image_captions: regularCaptions,
        video_url: mode === "regular" && mediaType === "video" ? mediaUrl : null,
        document_url: mode === "regular" && mediaType === "document" ? mediaUrl : null,
        document_name: mode === "regular" && mediaType === "document" ? mediaName : null,
        media_type:
          mode === "regular"
            ? (regularImages.length > 0 ? "image" : mediaType)
            : null,
        proof_image_url: mode === "income_proof" && imageUrls[0] ? imageUrls[0] : null,
        amount: mode === "income_proof" ? Number(amount) : null,
        timeframe: mode === "income_proof" ? timeframe : null,
      };
      const { data } = await api.post("/posts", payload);
      onPosted?.(data);
      setContent("");
      removeMedia();
      setAmount("");
    } finally {
      setPosting(false);
    }
  };

  const generateCaption = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/caption", {
        prompt: content || `Share a quick ${niche} tip for beginners.`,
        niche,
      });
      setContent(data.caption);
    } catch {} finally { setAiBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="surface rounded-xl p-5 mb-6"
      data-testid="composer"
    >
      <div className="flex items-center gap-2 mb-4 text-xs font-mono">
        <button
          data-testid="composer-mode-regular"
          onClick={() => setMode("regular")}
          className={`px-3 py-1.5 rounded-md uppercase tracking-wider transition ${
            mode === "regular" ? "bg-white text-black" : "text-white/50 hover:text-white"
          }`}
        >
          Post
        </button>
        <button
          data-testid="composer-mode-proof"
          onClick={() => setMode("income_proof")}
          className={`px-3 py-1.5 rounded-md uppercase tracking-wider transition flex items-center gap-1.5 ${
            mode === "income_proof" ? "cta-green" : "text-white/50 hover:text-white"
          }`}
        >
          <DollarSign size={12} strokeWidth={2.5} /> Income Proof
        </button>
      </div>

      <div className="flex gap-3 items-start">
        <img
          src={user?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${user?.user_id}`}
          alt=""
          className="w-10 h-10 rounded-md object-cover bg-white/5 shrink-0"
        />
        <div className="flex-1">
          {mode === "income_proof" && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00E676] font-mono">$</span>
                <input
                  data-testid="composer-amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="w-full bg-white/[0.04] border border-white/8 rounded-md pl-7 pr-3 py-2.5 font-mono text-lg text-[#00E676] focus:outline-none focus:border-[#00E676]/40"
                />
              </div>
              <select
                data-testid="composer-timeframe"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#00E676]/40"
              >
                {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          <textarea
            data-testid="composer-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={mode === "income_proof" ? "How did you earn it? Share the playbook." : "Drop a tip, ask a question, share a win…"}
            rows={3}
            className="w-full bg-transparent text-white placeholder:text-white/30 resize-none focus:outline-none text-[15px] leading-relaxed"
          />

          {imageUrls.length > 0 && (
            <div className="mt-2" data-testid="composer-image-gallery">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">
                  {imageUrls.length}/{MAX_IMAGES} photos · drag to reorder
                </span>
                <button
                  onClick={() => { setImageUrls([]); setImageCaptions([]); }}
                  className="text-[10px] font-mono uppercase tracking-wider text-white/40 hover:text-white transition"
                  data-testid="composer-clear-images"
                >
                  Clear all
                </button>
              </div>
              <div
                className={`grid gap-2 ${
                  imageUrls.length === 1
                    ? "grid-cols-1"
                    : imageUrls.length === 2
                    ? "grid-cols-2"
                    : "grid-cols-3"
                }`}
              >
                {imageUrls.map((url, i) => (
                  <div
                    key={url + i}
                    draggable
                    onDragStart={(e) => {
                      setDragIdx(i);
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* older browsers */ }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIdx ?? Number(e.dataTransfer.getData("text/plain"));
                      moveImage(from, i);
                      setDragIdx(null);
                    }}
                    onDragEnd={() => setDragIdx(null)}
                    className={`relative group rounded-lg overflow-hidden border transition ${
                      dragIdx === i
                        ? "border-[#00E676]/60 opacity-60"
                        : "border-white/8 hover:border-white/20"
                    }`}
                    data-testid={`composer-image-tile-${i}`}
                  >
                    <div className="relative aspect-square bg-black">
                      <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
                      <div
                        className="absolute top-1.5 left-1.5 bg-black/70 rounded-md p-1 cursor-grab active:cursor-grabbing"
                        title="Drag to reorder"
                      >
                        <GripVertical size={12} className="text-white/70" />
                      </div>
                      {i === 0 && (
                        <div className="absolute bottom-1.5 left-1.5 bg-[#00E676] text-black text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                          Cover
                        </div>
                      )}
                      <button
                        data-testid={`composer-remove-image-${i}`}
                        onClick={() => removeImageAt(i)}
                        className="absolute top-1.5 right-1.5 bg-black/70 rounded-md p-1 hover:bg-black"
                        title="Remove image"
                      >
                        <X size={11} />
                      </button>
                    </div>
                    <input
                      value={imageCaptions[i] || ""}
                      onChange={(e) => setCaptionAt(i, e.target.value.slice(0, 120))}
                      placeholder="Add a caption…"
                      maxLength={120}
                      className="w-full bg-white/[0.04] border-t border-white/8 px-2.5 py-1.5 text-[11px] text-white placeholder:text-white/25 focus:outline-none focus:bg-white/[0.06]"
                      data-testid={`composer-caption-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {mediaUrl && (
            <div className="relative mt-2 rounded-lg overflow-hidden border border-white/8" data-testid="composer-media-preview">
              {mediaType === "video" ? (
                <video src={mediaUrl} controls className="w-full max-h-96 bg-black" />
              ) : mediaType === "document" ? (
                <div className="flex items-center gap-3 p-4 surface-elev">
                  <div className="w-10 h-10 rounded-md bg-[#00E676]/10 border border-[#00E676]/25 flex items-center justify-center">
                    <FileText size={18} className="text-[#00E676]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{mediaName || "Document.pdf"}</div>
                    <div className="text-[11px] font-mono text-white/40">
                      PDF · {(mediaSize / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  </div>
                </div>
              ) : null}
              <button
                data-testid="composer-remove-media"
                onClick={removeMedia}
                className="absolute top-2 right-2 bg-black/70 rounded-md p-1.5 hover:bg-black"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {uploading && (
            <div className="mt-2 surface-elev rounded-md p-3 flex items-center gap-3 text-xs text-white/70">
              <Loader2 size={14} className="animate-spin text-[#00E676] shrink-0" />
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span>{uploadLabel}</span>
                  <span className="font-mono text-[#00E676]">{uploadPct}%</span>
                </div>
                <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#00E676] transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            </div>
          )}

          <input
            ref={imageInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            data-testid="composer-image-input"
            onChange={(e) => uploadImages(e.target.files)}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            data-testid="composer-video-input"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <input
            ref={docInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="composer-doc-input"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />

          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 mr-1">Niche</span>
            {NICHES.map((n) => (
              <button
                key={n}
                data-testid={`composer-niche-${n}`}
                onClick={() => setNiche(n)}
                className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border transition ${
                  niche === n
                    ? "border-[#00E676] text-[#00E676] bg-[#00E676]/8"
                    : "border-white/8 text-white/40 hover:text-white"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5 gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <button
                data-testid="composer-upload-image"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploading || imageUrls.length >= MAX_IMAGES}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-white/60 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                title={`Upload images (up to ${MAX_IMAGES})`}
              >
                <ImageIcon size={14} className="text-[#00E676]" />
                {imageUrls.length > 0 ? `Photos · ${imageUrls.length}` : "Photos"}
              </button>
              <button
                data-testid="composer-upload-video"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-white/60 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                title="Upload video (max 100MB)"
              >
                <Video size={14} className="text-[#00E676]" /> Video
              </button>
              <button
                data-testid="composer-upload-doc"
                onClick={() => docInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-white/60 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                title="Upload PDF (max 25MB)"
              >
                <FileText size={14} className="text-[#00E676]" /> PDF
              </button>
              <button
                data-testid="composer-ai-caption"
                onClick={generateCaption}
                disabled={aiBusy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-white/60 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
              >
                <Wand2 size={14} className="text-[#00E676]" />
                {aiBusy ? "Writing…" : "AI Assist"}
              </button>
            </div>
            <button
              data-testid="composer-submit"
              disabled={posting || uploading}
              onClick={submit}
              className="cta-green rounded-md px-5 py-2 font-semibold text-sm disabled:opacity-50"
            >
              {posting ? "Posting…" : mode === "income_proof" ? "Drop Proof" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
